import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { useNavigate } from 'react-router-dom';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { createSelfbookHandler } from '../../utils/selfbook';
import './TheHotelAtacama.css';

const TheHotelAtacama: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleBooking = createSelfbookHandler(navigate, '/atacama/contact');
  const handlePatagoniaBooking = createSelfbookHandler(navigate, '/patagonia/contact');
  const handleIguazuBooking = createSelfbookHandler(navigate, '/iguazu/contact');
  const handleMendozaBooking = createSelfbookHandler(navigate, '/mendoza/contact');
  const handleSantaCatarinaBooking = createSelfbookHandler(navigate, '/santa-catarina/contact');

  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  return (
    <div className="the-hotel-page">
      <UnifiedNavigation variant="light" destination="atacama" />
      
      {/* Hero Section */}
      <section className="hotel-hero" data-scroll-section="hero">
        <div className="hotel-hero__container">
          <h1 className="hotel-hero__title">{t('theHotel.hero.title')}</h1>
          <p className="hotel-hero__subtitle">
            {t('theHotel.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* How it's started Section */}
      <section className="hotel-story-section" data-scroll-section="story">
        <div className="story-container">
          <div className="story-image">
            <img src={images.atacamaHotel1 || images.desertLandscape} alt="Awasi Atacama historical site" />
          </div>
          <div className="story-content">
            <h2>{t('theHotel.story.title')}</h2>
            <p>
              {t('theHotel.story.paragraph1')}
            </p>
            <p>
              {t('theHotel.story.paragraph2')}
            </p>
          </div>
        </div>
      </section>

      {/* Privacy and relaxation Section */}
      <section className="hotel-comfort-section" data-scroll-section="comfort">
        <div className="comfort-container">
          <div className="comfort-content">
            <h2>{t('theHotel.comfort.title')}</h2>
            <p>
              {t('theHotel.comfort.description')}
            </p>
          </div>
          <div className="comfort-image">
            <img src={images.masterVilla || images.villaExterior} alt="Awasi Atacama room interior" />
          </div>
        </div>
      </section>

      {/* Endless possibilities Section */}
      <section className="hotel-possibilities-section" data-scroll-section="possibilities">
        <div className="possibilities-container">
          <div className="possibilities-image">
            <img src={images.atacamaExperienceCard1 || images.video1} alt="Desert excursions" />
          </div>
          <div className="possibilities-content">
            <h2>{t('theHotel.possibilities.title')}</h2>
            <p>
              {t('theHotel.possibilities.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="hotel-map-section" data-scroll-section="map">
        <div className="map-container">
          <div className="map-image">
            <img src={images.mapRectangle8767} alt="Atacama location map" />
            <div className="map-marker">
              <img src="https://images.ctfassets.net/hz1mviiqoqof/7nslKOISxz2JnsAy0y0knc/179b83c8c6d31ab440e8e6eb8dd7d386/mapMarkerAwasi.svg" alt="Map Marker" />
            </div>
          </div>
          <div className="map-content">
            <h3>{t('theHotel.map.title')}</h3>
            <div className="map-info-sections">
              <div className="map-info-section">
                <h4>{t('theHotel.map.fromSantiago.title')}</h4>
                <p>
                  {t('theHotel.map.fromSantiago.description')}
                </p>
              </div>
              <div className="map-info-section">
                <h4>{t('theHotel.map.borders.title')}</h4>
                <p>
                  {t('theHotel.map.borders.bolivia')}<br/>
                  {t('theHotel.map.borders.argentina')}
                </p>
              </div>
            </div>
            <div className="map-buttons">
              <HandDrawnButton
                text={t('theHotel.map.contactUs')}
                onClick={handleBooking}
                variant="atacama"
                color="awasi"
                animation="stroke-to-fill"
                size="normal"
                className="hotel-contact-button"
              />
              <HandDrawnButton
                text={t('theHotel.map.learnMore')}
                onClick={() => navigate('/atacama/getting-here')}
                variant="atacama"
                color="awasi"
                animation="stroke-to-stroke"
                size="normal"
                className="hotel-learn-button"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="hotel-team-section" data-scroll-section="team">
        <div className="team-container">
          <h2 className="team-title">{t('theHotel.team.title')}</h2>
          <p className="team-description">
            {t('theHotel.team.description')}
          </p>
          
          <div className="team-grid">
            <div className="team-member">
              <img src={images.teamMember2 || images.instagram2} alt="Sebastián Prieto" />
              <h4>{t('theHotel.team.member.name')}</h4>
              <p className="team-member-role">{t('theHotel.team.member.role')}</p>
              <p className="team-member-description">
                {t('theHotel.team.member.description')}
              </p>
              <CTALink 
                text={t('theHotel.team.readMore')}
                type="secondary"
                color="awasi"
                onClick={() => {}} // Placeholder - needs proper link
                className="team-member-link"
              />
            </div>
          </div>
          
          <div className="team-cta">
            <HandDrawnButton
              text={t('theHotel.team.joinTeam')}
              onClick={() => navigate('/careers')}
              variant="atacama"
              color="off-white"
              animation="stroke-to-fill"
              size="wide"
              className="hotel-careers-button"
            />
          </div>
        </div>
      </section>

      {/* Equality, Diversity & Inclusion Section */}
      <section className="hotel-values-section" data-scroll-section="values">
        <div className="values-container">
          <h2>{t('theHotel.values.title')}</h2>
          
          <div className="values-content">
            <div className="value-item">
              <h3>{t('theHotel.values.internship.title')}</h3>
              <p>
                {t('theHotel.values.internship.description')}
              </p>
              <CTALink 
                text={t('theHotel.values.readMore')}
                type="secondary"
                color="awasi"
                onClick={() => {}} // Placeholder - needs proper link
                className="value-link"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Press & Awards Section */}
      <section className="hotel-press-section" data-scroll-section="press">
        <div className="press-container">
          <h2>Press & Awards</h2>
          
          <div className="awards-list">
            <div className="award-item">
              <h4>Travel + Leisure The Top 100 Hotels in the World 2020</h4>
              <p>AWASI - AWASI ATACAMA</p>
            </div>
            <div className="award-item">
              <h4>Conde Nast Traveller Gold List 2019</h4>
              <p>AWASI - AWASI IGUAZÚ</p>
            </div>
            <div className="award-item">
              <h4>The Telegraph The world's 50 greatest hotels</h4>
              <p>AWASI - AWASI PATAGONIA</p>
            </div>
            <div className="award-item">
              <h4>Hollywood's International Documentary Festival</h4>
              <p>AWASI - AWASI IGUAZÚ</p>
            </div>
            <div className="award-item">
              <h4>The Tripadvisor 2024 Travelers' Choice Awards - Best of the Best</h4>
              <p>AWASI - AWASI SANTA CATARINA</p>
            </div>
            <div className="award-item">
              <h4>Wine Spectator 2025 Award of Excellence</h4>
              <p>AWASI - AWASI MENDOZA</p>
            </div>
          </div>
          
          <div className="press-cta">
            <CTALink 
              text="SEE MORE"
              type="secondary"
              color="awasi"
              onClick={() => navigate('/press-awards')}
              className="press-see-more-link"
            />
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="hotel-destinations-section" data-scroll-section="destinations">
        <div className="destinations-container">
          <h2>A Meaningful Exploration Centered Around You</h2>
          <p className="destinations-description">
            Awasi offers tailor-made journeys so each traveller can explore at their own pace and connect meaningfully with each destination. We are here to guide you on this path of discovery, and help you create memorable, personal and authentic experiences.
          </p>
          
          <div className="destinations-cta">
            <CTALink 
              text="SEE DESTINATIONS"
              type="secondary"
              color="awasi"
              onClick={() => navigate('/destinations')}
              className="destinations-see-link"
            />
          </div>
          
          <div className="destinations-grid">
            <div className="destination-card">
              <img src={images.patagoniaCard} alt="Patagonia" />
              <h3>Patagonia</h3>
              <span className="destination-tag">SOUTHERN CHILE</span>
              <div className="destination-buttons">
                <CTALink 
                  text="VISIT THE PROPERTY" 
                  type="secondary" 
                  color="awasi" 
                  onClick={() => navigate('/patagonia')}
                />
                <CTALink 
                  text="BOOK YOUR STAY" 
                  type="primary" 
                  color="awasi" 
                  onClick={handlePatagoniaBooking}
                />
              </div>
            </div>
            
            <div className="destination-card">
              <img src={images.iguazuCard} alt="Iguazú" />
              <h3>Iguazú</h3>
              <span className="destination-tag">NORTHERN ARGENTINA</span>
              <div className="destination-buttons">
                <CTALink 
                  text="VISIT THE PROPERTY" 
                  type="secondary" 
                  color="awasi" 
                  onClick={() => navigate('/iguazu')}
                />
                <CTALink 
                  text="BOOK YOUR STAY" 
                  type="primary" 
                  color="awasi" 
                  onClick={handleIguazuBooking}
                />
              </div>
            </div>
            
            <div className="destination-card">
              <img src={images.mendozaCard} alt="Mendoza" />
              <h3>Mendoza</h3>
              <span className="destination-tag">WESTERN ARGENTINA</span>
              <div className="destination-buttons">
                <CTALink 
                  text="VISIT THE PROPERTY" 
                  type="secondary" 
                  color="awasi" 
                  onClick={() => navigate('/mendoza')}
                />
                <CTALink 
                  text="BOOK YOUR STAY" 
                  type="primary" 
                  color="awasi" 
                  onClick={handleMendozaBooking}
                />
              </div>
            </div>
            
            <div className="destination-card">
              <img src={images.santaCatarinaCard} alt="Santa Catarina" />
              <h3>Santa Catarina</h3>
              <span className="destination-tag">SOUTHERN BRAZIL</span>
              <div className="destination-buttons">
                <CTALink 
                  text="VISIT THE PROPERTY" 
                  type="secondary" 
                  color="awasi" 
                  onClick={() => navigate('/santa-catarina')}
                />
                <CTALink 
                  text="BOOK YOUR STAY" 
                  type="primary" 
                  color="awasi" 
                  onClick={handleSantaCatarinaBooking}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <InstagramSection 
        title={t('instagram.title')}
        posts={[
          { src: images.film13, alt: "Instagram 1" },
          { src: images.filmVideo1, alt: "Instagram 2", isVideo: true },
          { src: images.film14, alt: "Instagram 3" },
          { src: images.film15, alt: "Instagram 4" }
        ]}
      />

      <Footer variant="atacama" />
    </div>
  );
};

export default TheHotelAtacama;